import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { DocEntityHandle, RoofHandle, SkylightHandle } from "building-types/handles/docEntityHandleTypes";
import { type OpeningSide } from "building-types/openings/openingTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { GenerateRoofSkylightOptions } from "building-types/roofs/roofGenerationTypes";
import type { SkylightPlacement } from "building-types/roofs/roofSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import {
  BuildingSide,
  RoofSide,
  type Skylight,
  type SkylightOpening,
  type SkylightUnit,
} from "building-types/settings/roofTypes";
import type { WallSettings } from "building-types/settings/wallSettings";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { RWTable } from "common/data/table";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { exteriorMaterialOverrideNew, interiorMaterialOverrideNew } from "doc/utils/materialTools";
import { MULTIGABLE_BSP_EPSILON, skylightNew } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2Height } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { faceNew, faceOrNullCopy, faceOrNullMoveAlongNormal, faceOrNullReverse } from "geom/brep";
import { buildBSPNode } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { addEdgeToPolygonList, clippingNodeNew } from "geom/bspClipping";
import type { PolygonBSP } from "geom/bspTypes";
import { extrudeFaceAlongNormal } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeCopy,
  planeEval,
  planeMoveAlongNormal,
  planeNewXY,
  planeProject,
  planeProjectPolyLine3,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2AABB2, polyLine2Copy, polyLine2EdgeCount, polyLine2Reverse } from "geom/polyLine2";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Copy,
  polyLine3EvalEdge,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3OnPlane,
} from "geom/polyLine3";
import { rect2ByTwoPoints } from "geom/rect2";
import { vec2Dist, vec2Len } from "geom/vec2";
import { makeOpeningSide } from "../../elements/openings/openingProfile";
import { getCombinedMirrorAxes, isCombinedGlobalInstanceMirror } from "../../generationTools/generateOpeningsTools";
import { convertDormerDistParam } from "../multiGableRoofTools";
import { roofFaceBuildingSideWithFlips } from "../roofFaceBuildingSide";
import { clipRoofFaces } from "./roofClipping";
import { getRoofEdgeFromSide } from "./roofEdges";
import { mergeCoplanarRoofTopFaces } from "./roofFacesMerge";
import { getPeakExtension, isValidExtension } from "./roofSlopes";

const SKYLIGHT_INTERIOR_FRAME_WIDTH = 3.5;
const SKYLIGHT_EXTERIOR_FRAME_WIDTH = 3.5;
const SKYLIGHT_INTERIOR_FRAME_DEPTH = 0.75;
const SKYLIGHT_EXTERIOR_FRAME_DEPTH = 3.5;

/**
 * Generates the skylights attached to this roof
 */
export function buildRoofSkylights(
  subRoof: GeneratedRoof,
  subRoofOptions: GenerateRoofSkylightOptions,
): {
  subRoof: GeneratedRoof;
  skylights: RWTable<Skylight>;
  skylightUnits: RWTable<SkylightUnit>;
} {
  const { wallSettings, mirror = false, skylightPlacements = [], issues = [] } = subRoofOptions;

  const { roofVolume, rails } = subRoof;
  const { extrusion, extensions } = roofVolume;
  const { startLeftExtensions, startRightExtensions, endLeftExtensions, endRightExtensions } = extensions;
  const { leftRail, rightRail, startRail, endRail } = rails;
  const { leftFaces, rightFaces, startHipFaces, endHipFaces } = extrusion;

  const skylights: RWTable<Skylight> = {};
  const skylightUnits: RWTable<SkylightUnit> = {};

  const combinedMirror = isCombinedGlobalInstanceMirror(mirror, subRoof);
  const { combinedFlipX, combinedFlipY } = getCombinedMirrorAxes(mirror, subRoof?.componentTransform);

  // helper function to add skylights to a side
  const tryToAddSkylight = (subRoof: GeneratedRoof, skylight: SkylightPlacement, roofSide: RoofSide): GeneratedRoof => {
    const { side = BuildingSide.FRONT } = skylight;

    let faces: Face[] = [];
    let startExtensions: number[] = [];
    let endExtensions: number[] = [];
    let rail3: PolyLine3 | undefined;

    switch (roofSide) {
      case RoofSide.LEFT:
        faces = leftFaces;
        startExtensions = startLeftExtensions;
        endExtensions = endLeftExtensions;
        rail3 = leftRail;
        break;
      case RoofSide.RIGHT:
        faces = rightFaces;
        startExtensions = startRightExtensions;
        endExtensions = endRightExtensions;
        rail3 = rightRail;
        break;
      case RoofSide.START:
        faces = startHipFaces;
        rail3 = startRail;
        break;
      case RoofSide.END:
        faces = endHipFaces;
        rail3 = endRail;
        break;
      default:
        break;
    }

    if (faces.length === 0 || !rail3) {
      return subRoof;
    }

    const faceSide = roofFaceBuildingSideWithFlips(faces[0], combinedFlipX, combinedFlipY);

    if (faceSide !== side) {
      return subRoof;
    }

    return addSkylights({
      subRoof,
      rail3,
      startExtensions,
      endExtensions,
      side: roofSide,
      faces,
      faceSide,
      mirror: combinedMirror,
      skylight,
      wallSettings,
      skylights,
      skylightUnits,
      issues,
    });
  };

  let newSubRoof = subRoof;
  for (const skylight of skylightPlacements) {
    newSubRoof = tryToAddSkylight(newSubRoof, skylight, RoofSide.LEFT);
    newSubRoof = tryToAddSkylight(newSubRoof, skylight, RoofSide.RIGHT);
    newSubRoof = tryToAddSkylight(newSubRoof, skylight, RoofSide.START);
    newSubRoof = tryToAddSkylight(newSubRoof, skylight, RoofSide.END);
  }

  return {
    skylights,
    skylightUnits,
    subRoof: newSubRoof,
  };
}

export interface AddSkylightsOptions {
  readonly subRoof: GeneratedRoof;
  readonly rail3: PolyLine3;
  readonly side: RoofSide;
  readonly faces: Face[];
  readonly skylight: SkylightPlacement;
  readonly wallSettings: WallSettings;
  readonly faceSide: BuildingSide;
  readonly mirror: boolean;
  readonly startExtensions?: number[];
  readonly endExtensions?: number[];
  readonly issues: BuildingGenerationIssue[];
  readonly skylights: RWTable<Skylight>;
  readonly skylightUnits: RWTable<SkylightUnit>;
}

export function addSkylights({
  subRoof,
  rail3,
  side,
  faces,
  faceSide,
  mirror,
  skylight,
  wallSettings,
  startExtensions = [],
  endExtensions = [],
  skylights,
  skylightUnits,
  issues,
}: AddSkylightsOptions): GeneratedRoof {
  const {
    normalizedPositions = false,
    verticalPosition = 40,
    horizontalPositions,
    useNewPlacementMethod = false,
  } = skylight;

  const {
    roofVolume,
    roofSettings: { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING },
  } = subRoof;

  const flipDistances = (faceSide === BuildingSide.FRONT || faceSide === BuildingSide.BACK) !== mirror;

  const skylightWidth = getSkylightWidth(skylight);

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(wallSettings, overhangDatum);

  const hasStartExtension = isValidExtension(startExtensions);
  const hasEndExtension = isValidExtension(endExtensions);

  const skylightRail = polyLine3Copy(rail3);
  polyLine3ExtendBothEnds(skylightRail, -skylightWidth * 0.5, -skylightWidth * 0.5);

  const edge = getRoofEdgeFromSide(roofVolume, side);
  assertIsNotNullOrUndefined(edge);

  const wallToRoofEdgeDistance = edge.sideOverhang - exteriorWallOffsetWithSheathing;

  if (hasStartExtension) {
    polyLine3ExtendStart(skylightRail, getPeakExtension(startExtensions));
  } else {
    polyLine3ExtendStart(skylightRail, -wallToRoofEdgeDistance);
  }

  if (hasEndExtension) {
    polyLine3ExtendEnd(skylightRail, getPeakExtension(endExtensions));
  } else {
    polyLine3ExtendEnd(skylightRail, -wallToRoofEdgeDistance);
  }

  const railLength = polyLine3Length(skylightRail);

  const skyLightUs: number[] = [];
  if (horizontalPositions) {
    skyLightUs.push(
      ...horizontalPositions.map((u) => {
        return convertDormerDistParam(u, railLength, 0.0, normalizedPositions);
      }),
    );
  } else {
    skyLightUs.push(railLength / 2);
  }

  let newSubRoof = subRoof;
  for (const skyLightU of skyLightUs) {
    let t = skyLightU;
    if (!useNewPlacementMethod && flipDistances) {
      t = railLength - skyLightU;
    }

    newSubRoof = addSkylightOnFaces({
      subRoof: newSubRoof,
      skylightRail,
      side,
      faces,
      skylight,
      wallSettings,
      issues,
      skylights,
      skylightUnits,
      u: t,
      v: verticalPosition,
    });
  }

  return newSubRoof;
}

function makeSkylightUnit(skylight: Skylight, entityHandleForSelectingReportingUnit: DocEntityHandle): SkylightUnit {
  const opening = skylight.opening;

  assertIsNotNullOrUndefined(opening);

  const exteriorFacePlane = opening.backPlane;
  const exteriorOutline = opening.backHole.opening2;
  const interiorFacePlane = opening.frontPlane;
  const interiorOutline = opening.frontHole.opening2;

  const unit = skylightUnitNew(skylight, entityHandleForSelectingReportingUnit);
  const exteriorOutlineReversed = polyLine2Reverse(polyLine2Copy(exteriorOutline));

  // make glassPlane inset from wall
  const glassPlane = planeMoveAlongNormal(planeCopy(exteriorFacePlane), SKYLIGHT_EXTERIOR_FRAME_DEPTH - 0.2);

  // interior frame
  const interiorWindowFrame = faceNew(
    [polyLine2Reverse(offsetPolyLine2(interiorOutline, SKYLIGHT_INTERIOR_FRAME_WIDTH)), interiorOutline],
    interiorFacePlane,
  );

  unit.interior.add(
    extrudeFaceAlongNormal(interiorWindowFrame, SKYLIGHT_INTERIOR_FRAME_DEPTH),
    interiorMaterialOverrideNew("trimInterior0"),
  );

  const exteriorFrameOutline = offsetPolyLine2(exteriorOutline, -SKYLIGHT_EXTERIOR_FRAME_WIDTH);

  const exteriorFrame = faceNew([exteriorOutlineReversed, exteriorFrameOutline], exteriorFacePlane);

  unit.exterior.add(
    extrudeFaceAlongNormal(exteriorFrame, SKYLIGHT_EXTERIOR_FRAME_DEPTH),
    exteriorMaterialOverrideNew("trimExterior0"),
  );

  // glass
  const glass = faceNew([exteriorOutlineReversed], glassPlane);
  unit.glass.push(glass);

  // face for hidding the interior
  const hideInteriorFace = faceNew([exteriorOutlineReversed], planeCopy(glassPlane));
  faceOrNullMoveAlongNormal(hideInteriorFace, -1);
  unit.hideInteriorFaces.push(hideInteriorFace);

  return unit;
}

function getFinalSkylightPosition(skylight: Skylight, edgeLength: number): OpeningSide {
  const { width, height, param, paramNormalized, verticalOffset } = skylight;

  let centerParam = param;

  if (paramNormalized) {
    centerParam = edgeLength * centerParam;
  }

  const top = verticalOffset + height;
  const bottom = verticalOffset;

  const halfWidth = width * 0.5;
  const startParam = centerParam - halfWidth;
  const endParam = centerParam + halfWidth;

  const openingRect2 = rect2ByTwoPoints(startParam, bottom, endParam, top);

  return makeOpeningSide({
    opening2: openingRect2,
    openingRect2,
    startParam,
    endParam,
    context: {
      condition: "EXTERIOR",
    },
  });
}

export interface AddSkylightOnFacesOptions {
  readonly subRoof: GeneratedRoof;
  readonly side: RoofSide;
  readonly faces: Face[];
  readonly skylightRail: PolyLine3;
  readonly skylight: SkylightPlacement;
  readonly wallSettings: WallSettings;
  readonly issues: BuildingGenerationIssue[];
  readonly skylights: RWTable<Skylight>;
  readonly skylightUnits: RWTable<SkylightUnit>;
  readonly u?: number;
  readonly v?: number;
  readonly offsetU?: number;
  readonly offsetV?: number;
}

export function addSkylightOnFaces({
  subRoof,
  side,
  faces,
  skylightRail,
  skylight,
  wallSettings,
  issues,
  skylights,
  skylightUnits,
  u = 100,
  v = 20,
}: AddSkylightOnFacesOptions): GeneratedRoof {
  const getFaceSlope = (face: Face): number => {
    const { vX, vY, vZ } = face.plane;
    const dist = vec2Len(vX, vY);

    return vZ / dist;
  };

  if (!subRoof || faces.length === 0) {
    return subRoof;
  }

  const railLength = polyLine3Length(skylightRail);

  const {
    id: roofId,
    roofVolume,
    roofTopFaces,
    roofSettings: { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING },
  } = subRoof;

  const baseFace = faces[0];
  const baseSlope = getFaceSlope(baseFace);

  // build the roof handle
  const handle: RoofHandle = {
    type: "ROOF",
    id: roofId,
  };

  const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, handle);

  // get the skylight dimensions
  const width = getSkylightWidth(skylight);
  const height = getSkylightHeight(skylight);

  if (u < 0 || u > railLength) {
    issues.push({
      type: BuildingGenerationIssueType.SKYLIGHT_OPENING_EXCEEDS_BOUNDARY,
      handle,
      params: [
        {
          type: "RANGE",
          description: "skylight horizontal position",
          value: width,
          max: railLength,
          issueType: "MAX_EXCEEDED",
        },
      ],
    });

    return subRoof;
  }

  const wallOffset = getExteriorWallOffset(wallSettings, overhangDatum);
  const wallToRoofEdgeDistance = ensureDefined(getRoofEdgeFromSide(roofVolume, side)).sideOverhang - wallOffset;

  const offsetV = vec2Dist(0, 0, wallToRoofEdgeDistance, wallToRoofEdgeDistance * baseSlope);

  v += offsetV;

  const [sx, sy, sz] = polyLine3EvalEdge(skylightRail, 0, u, false);
  [u] = planeProject(baseFace.plane, sx, sy, sz);
  const [x, y, z] = planeEval(baseFace.plane, u, v);

  // find the slope for the current vertical offset
  let faceIndex = -1;
  let accumAdvDist = 0;

  for (let i = 0, l = faces.length; i < l; i++) {
    const face = faces[i];
    const aabb2 = polyLine2AABB2(face.loops[0]);
    accumAdvDist += aabb2Height(aabb2);

    if (v < accumAdvDist) {
      faceIndex = i;
      break;
    }
  }

  if (faceIndex < 0 || (faceIndex >= faces.length - 1 && v + height > accumAdvDist)) {
    issues.push({
      type: BuildingGenerationIssueType.SKYLIGHT_OPENING_EXCEEDS_BOUNDARY,
      handle,
      params: [
        {
          type: "RANGE",
          description: "skylight vertical position",
          value: v - offsetV,
          max: accumAdvDist - offsetV - height,
          issueType: "MAX_EXCEEDED",
        },
      ],
    });

    return subRoof;
  }

  const face = faces[faceIndex];

  // build the skylight
  planeEval(face.plane, u, v);
  const [finalU, finalV] = planeProject(face.plane, x, y, z);
  const skylightHandle: SkylightHandle = { type: "SKYLIGHT", id: skylight.id };
  // A skylight in a component instance is selected as the instance, like the roof hosting it;
  // elsewhere it is selected as the skylight itself.
  const skylightPickHandle: DocEntityHandle =
    subRoof.pickHandle?.type === "COMPONENT_INSTANCE" ? subRoof.pickHandle : skylightHandle;

  let generatedSkylight = skylightNew(skylightPickHandle, subRoof.id, subRoof.levelId, width, height, finalV, finalU);

  // build the hole data
  const holeData = getFinalSkylightPosition(generatedSkylight, railLength);

  // build the skylight opening
  const holeOutline = holeData.opening2;
  const holeOutline3 = polyLine3OnPlane(polyLine2Reverse(holeOutline), face.plane);

  const backFace = ensureDefined(
    faceOrNullMoveAlongNormal(faceOrNullReverse(faceOrNullCopy(face)), GABLE_ROOF_THICKNESS),
  );

  const opening2Reverse = polyLine2Reverse(holeOutline);

  generatedSkylight = {
    ...generatedSkylight,
    opening: skylightOpeningNew(
      makeOpeningSide({
        opening2: opening2Reverse,
        openingRect2: opening2Reverse,
        context: {
          condition: "EXTERIOR",
          openingPlane: face.plane,
        },
      }),
      makeOpeningSide({
        opening2: holeOutline,
        openingRect2: holeOutline,
        context: {
          condition: "INTERIOR",
          // @todo(Andrew): what should the back plane of a skylight be?
          openingPlane: backFace.plane,
        },
      }),
      holeOutline3,
      polyLine3OnPlane(holeOutline, backFace.plane),
      backFace.plane,
      face.plane,
    ),
  };

  // build the BSP for clipping the roof
  const worldSpaceHoleOutline = planeProjectPolyLine3(planeNewXY(), holeOutline3);
  const holePolygons: PolygonBSP[] = [];
  for (let i = 0, l = polyLine2EdgeCount(worldSpaceHoleOutline); i < l; i++) {
    addEdgeToPolygonList(worldSpaceHoleOutline, i, 0.0, 1.0, null, holePolygons, MULTIGABLE_BSP_EPSILON);
  }

  const holeBSP = buildBSPNode({
    polygons: holePolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });

  const options: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(holeBSP, true)],
  };

  // clip the roof tops
  const filterFunc = (x: RoofFace) => {
    return x.side === side;
  };

  let newRoofTopFaces = clipRoofFaces(roofTopFaces, options, filterFunc);

  // merge coplanar faces after clipping
  newRoofTopFaces = mergeCoplanarRoofTopFaces(newRoofTopFaces);

  // build the skylight unit
  const skylightUnit = makeSkylightUnit(generatedSkylight, handle);

  const generatedSkylightId = uid();
  skylights[generatedSkylightId] = generatedSkylight;
  skylightUnits[generatedSkylightId] = skylightUnit;

  return {
    ...subRoof,
    roofTopFaces: newRoofTopFaces,
  };
}

export function getSkylightWidth(skylightPlacement: SkylightPlacement): number {
  return skylightPlacement.width ?? 40;
}

function getSkylightHeight(skylightPlacement: SkylightPlacement): number {
  return skylightPlacement.height ?? 60;
}

function skylightUnitNew(skylight: Skylight, entityHandleForSelectingReportingUnit: DocEntityHandle): SkylightUnit {
  return {
    skylight,
    glass: [],
    hideInteriorFaces: [],
    interior: new GeomTagGroup(),
    exterior: new GeomTagGroup(),
    decorations: new GeomTagGroup(),
    entityHandleForSelectingReportingUnit,
  };
}

function skylightOpeningNew(
  frontHole: OpeningSide,
  backHole: OpeningSide,
  frontOutline3: PolyLine3,
  backOutline3: PolyLine3,
  frontPlane: Plane,
  backPlane: Plane,
): SkylightOpening {
  return {
    frontHole,
    backHole,
    frontOutline3,
    backOutline3,
    backPlane,
    frontPlane,
  };
}
